"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { useT } from "@/components/i18n-ui/provider";
import { MARKETING_NAV } from "./nav";

export function MobileMenu({ signedIn }: { signedIn: boolean }) {
  const t = useT("marketing");
  const [open, setOpen] = useState(false);
  const pathname = usePathname();
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [pathname]);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);
  return (
    <div className="lg:hidden">
      <button type="button" onClick={() => setOpen(true)} className="btn-ghost -me-2 px-2" aria-label={t("mobileMenu.open")} aria-expanded={open} aria-controls="mobile-menu">
        <Menu className="h-5 w-5" aria-hidden />
      </button>
      {open && (
        <div id="mobile-menu" className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t("mobileMenu.dialog")}>
          <div className="absolute inset-0 bg-ink-950/30 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="absolute inset-x-3 top-3 rounded-2xl border border-ink-200 bg-white p-5 shadow-lift">
            <div className="flex items-center justify-between">
              <span className="font-display text-lg font-semibold">{t("mobileMenu.title")}</span>
              <button ref={closeRef} type="button" onClick={() => setOpen(false)} className="btn-ghost -me-2 px-2" aria-label={t("mobileMenu.close")}>
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>
            <nav aria-label={t("mobileMenu.nav")} className="mt-3">
              <ul className="divide-y divide-ink-100">
                {MARKETING_NAV.map((n) => (
                  <li key={n.href}>
                    <Link href={n.href} aria-current={pathname === n.href ? "page" : undefined} className="block py-3 text-base font-medium text-ink-800 hover:text-brand-700">
                      {t(n.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
            <div className="mt-4 grid gap-2">
              {signedIn ? (
                <Link href="/dashboard" className="btn-primary py-2.5">{t("mobileMenu.goToDashboard")}</Link>
              ) : (
                <>
                  <Link href="/signup" className="btn-primary py-2.5">{t("header.createBlog")}</Link>
                  <Link href="/login" className="btn-secondary py-2.5">{t("header.signIn")}</Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
